import { EnvironmentProviders, inject, makeEnvironmentProviders } from '@angular/core';

import type { ToolbeltConfig } from '@mercadoni/toolbelt';
import { TOOLBELT_CONFIG } from '@mercadoni/toolbelt';

import { ClusterService } from '../../../core/services';
import { NF_CONTEXT } from '../../../core/services/context/toolbelt-context.service';

/**
 * Registers backends for @mercadoni/toolbelt's own remote-data widgets (remote-select,
 * remote-multiselect, autocomplete, file-upload — via `x-ui.source` / `x-ui.fileUpload.source`),
 * resolved internally by the library's ToolbeltHttpService (tb-widget.component.ts).
 *
 * This is a *different* registry from TOOLBELT_DATASOURCES/provideNfDatasources(): that one
 * drives our own `x-datasource` CRUD extension (NfDatasourceService). Both end up pointing at
 * the same backends, so keep this file's entries in sync with the sibling `*.datasource.ts`
 * files when adding a new service.
 *
 * We build TOOLBELT_CONFIG via a factory provider instead of calling
 * `provideToolbelt({ services })` with a plain object: ToolbeltServiceConfig.baseUrl is typed
 * as a static string (unlike our factory-based NfDatasourceRegistry), and provideToolbelt()
 * just stores whatever object it's given with `useValue` — built at module-eval time, before
 * Angular's injector exists, so it can't call inject(). A factory provider is the only way to
 * read ClusterService's *current* value before TOOLBELT_CONFIG is first resolved.
 *
 * CAVEAT: baseUrl is still just a snapshot — captured once, the first time TOOLBELT_CONFIG is
 * injected (effectively at app start, honoring whatever cluster was persisted in localStorage
 * by ClusterService). If the user switches cluster mid-session via the topbar/sidebar selector,
 * native remote-select/file-upload widgets keep hitting the previously-active cluster's Coulson
 * until the page reloads — unlike our custom x-datasource path (NfDatasourceService), which
 * re-reads ClusterService on every call. Closing this gap needs @mercadoni/toolbelt to accept
 * `baseUrl: () => string`, the way it already does for `token`.
 *
 * No `token` needed for coulson: its baseUrl is always an *.instaleap.io host, which the global
 * Auth0 HTTP interceptor (see isInstaleapHost in app.config.ts) already matches — the shared
 * HttpClient instance ToolbeltHttpService injects gets the bearer token attached the same way
 * CoulsonApiService's calls do today, without either service setting headers itself.
 *
 * `context` is passed through from NF_CONTEXT (the same factories registered via
 * provideNfContext() in app.config.ts) so tb-form's own `{{context.*}}` resolution — currently
 * just `field.default` — reads from the one place fury-ui already declares clientId/storeId/
 * userId/env, instead of a second copy.
 */
export function provideToolbeltServices(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: TOOLBELT_CONFIG,
      useFactory: (): ToolbeltConfig => ({
        services: {
          // ToolbeltHttpService does `baseUrl + url` with no slash normalization, so normalize
          // the configured base URL and keep source URLs relative (e.g. 'bff/get-bifrost-client-ids').
          coulson: { baseUrl: inject(ClusterService).coulsonBaseUrl().replace(/\/+$/, '') + '/' },
          // furyBaseUrl is optional per cluster (e.g. 'local' has none yet) — fall back to '' rather
          // than throw here, since this config is built eagerly even for clusters with no fury-backed
          // native widgets.
          fury: { baseUrl: (inject(ClusterService).furyBaseUrl() ?? '').replace(/\/+$/, '') + '/' },
        },
        context: inject(NF_CONTEXT),
      }),
    },
  ]);
}
